import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Download, Refresh } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { trackingApi } from '../../services/trackingApi';

export interface AnalyticsData {
  documentsOverTime: Array<{ date: string; documentCount: number; totalTime: number; uniqueDocuments: number }>;
  timeSpentPerDocument: Array<{ documentCode: string; documentType: string; totalTime: number; viewCount: number }>;
  activityTimeline: Array<{ date: string; sessionCount: number; totalTime: number; documentsViewed: number }>;
  documentTypeEngagement: Array<{ documentType: string; totalViews: number; totalTime: number }>;
  navigationPaths: Array<{ fromCode: string; toCode: string; count: number }>;
}

type Props = {
  userId: string;
  embedded?: boolean;
};

const UserAnalyticsPanel: React.FC<Props> = ({ userId, embedded }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [groupBy, setGroupBy] = useState('day');
  const [data, setData] = useState<AnalyticsData | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      const [documentsOverTime, timeSpent, activityTimeline, docTypeEngagement, navPaths] = await Promise.all([
        trackingApi.getDocumentsOverTime(userId, startDateStr, endDateStr),
        trackingApi.getTimeSpentPerDocument(userId, startDateStr, endDateStr, 20),
        trackingApi.getActivityTimeline(userId, startDateStr, endDateStr, groupBy),
        trackingApi.getDocumentTypeEngagement(userId, startDateStr, endDateStr),
        trackingApi.getNavigationPaths(userId, startDateStr, endDateStr, 30),
      ]);

      setData({
        documentsOverTime: documentsOverTime.data?.data || [],
        timeSpentPerDocument: timeSpent.data?.data || [],
        activityTimeline: activityTimeline.data?.data || [],
        documentTypeEngagement: docTypeEngagement.data?.data || [],
        navigationPaths: navPaths.data?.data || [],
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [userId, startDate, endDate, groupBy]);

  useEffect(() => {
    load();
  }, [load]);

  const startStr = startDate.format('YYYY-MM-DD');
  const endStr = endDate.format('YYYY-MM-DD');

  const handleExport = (type: 'csv' | 'json') => {
    const url =
      type === 'csv'
        ? trackingApi.exportCsvUrl(userId, startStr, endStr)
        : trackingApi.exportJsonUrl(userId, startStr, endStr);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!embedded ? (
        <Typography variant="h6" gutterBottom>
          Activity analytics
        </Typography>
      ) : null}

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={2}>
        <TextField
          label="Start"
          type="date"
          size="small"
          value={startStr}
          onChange={(e) => setStartDate(dayjs(e.target.value))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End"
          type="date"
          size="small"
          value={endStr}
          onChange={(e) => setEndDate(dayjs(e.target.value))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField select label="Group by" size="small" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} sx={{ minWidth: 120 }}>
          <MenuItem value="day">Day</MenuItem>
          <MenuItem value="week">Week</MenuItem>
          <MenuItem value="month">Month</MenuItem>
        </TextField>
        <Button startIcon={<Refresh />} onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Button startIcon={<Download />} onClick={() => handleExport('csv')}>
          Export CSV
        </Button>
        <Button startIcon={<Download />} onClick={() => handleExport('json')}>
          Export JSON
        </Button>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {!data ? (
        <Typography color="text.secondary">No analytics available.</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Documents over time
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={data.documentsOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="documentCount" name="Documents" stroke={theme.palette.primary.main} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Activity timeline
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={data.activityTimeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="sessionCount" name="Sessions" stroke={theme.palette.info.main} />
                      <Line type="monotone" dataKey="documentsViewed" name="Docs viewed" stroke={theme.palette.success.main} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Time spent per document (top 20)
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.timeSpentPerDocument}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="documentCode" hide />
                      <YAxis />
                      <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.documentCode || ''} />
                      <Bar dataKey="totalTime" name="Time (ms)" fill={theme.palette.secondary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Document type engagement
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.documentTypeEngagement}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="documentType" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="totalViews" name="Views" fill={theme.palette.primary.light} />
                      <Bar dataKey="totalTime" name="Time (ms)" fill={theme.palette.warning.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Navigation paths (top 30)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.navigationPaths.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No navigation data in this period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.navigationPaths.map((row, i) => (
                        <TableRow key={`${row.fromCode}-${row.toCode}-${i}`}>
                          <TableCell>{row.fromCode || '—'}</TableCell>
                          <TableCell>{row.toCode || '—'}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default UserAnalyticsPanel;
